import React from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { MatterBriefcase, ChevronRight, Check } from '@/assets/icons'
import { clsx } from 'clsx'
import { selectMoveChatDestinationsMattersSortedByName } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { kMoveToMatterLabel } from '@/matter/constants'

type MatterSubmenuProps = {
  isMobile: boolean
  setMattersContentOpen: (open: boolean) => void
  handleMatterSelect: (toMatterId: string, toMatterName?: string) => void
  fromMatterId: string | null
  mattersContentOpen: boolean
}

export const MatterSubmenu: React.FC<MatterSubmenuProps> = ({ setMattersContentOpen, handleMatterSelect, fromMatterId, isMobile }) => {
  const matters = useAppSelector(selectMoveChatDestinationsMattersSortedByName)
  // If there are no matters, show a disabled item
  if (!matters.length) {
    return (
      <Tooltip title="No matters to display" placement="top-start" offsetX={-2} offsetY={-20}>
        <DropdownMenu.Item
          disabled
          className={clsx(
            'group flex items-center gap-2.5 p-2 rounded-md text-sm font-medium leading-5 w-full focus:outline-none cursor-not-allowed',
            'text-true-gray-300 bg-transparent [&_svg]:text-true-gray-300',
            'hover:bg-true-gray-100 focus:bg-true-gray-100 data-[highlighted]:bg-true-gray-100 data-[highlighted]:text-true-gray-300'
          )}
        >
          <MatterBriefcase className="w-4 h-4 shrink-0" />
          <span className="whitespace-nowrap">{kMoveToMatterLabel}</span>
          <ChevronRight className="w-4 h-4 text-brand-neutral-600" />
        </DropdownMenu.Item>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger asChild>
        <button
          className="group flex items-center gap-2.5 p-2 rounded-md text-sm leading-6 w-full focus:outline-none cursor-pointer hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 data-[highlighted]:bg-brand-neutral-100"
          onClick={(e) => {
            // Prevent clicking on desktop from opening mobile matters content
            e.stopPropagation()
            // Open mobile matters content
            if (isMobile) {
              setMattersContentOpen(true)
            }
          }}
        >
          <MatterBriefcase className="w-4 h-4 shrink-0 text-brand-neutral-600 group-hover:text-brand-950 group-focus:text-brand-950 group-data-[highlighted]:text-brand-950 group-data-[state=open]:text-brand-950" />
          <span className="whitespace-nowrap text-brand-neutral-700 group-hover:text-brand-950 group-focus:text-brand-950 group-data-[highlighted]:text-brand-950 group-data-[state=open]:text-brand-950">
            {kMoveToMatterLabel}
          </span>
          <ChevronRight className="w-4 h-4 text-brand-neutral-600 group-hover:text-brand-950 group-focus:text-brand-950 group-data-[highlighted]:text-brand-950 group-data-[state=open]:text-brand-950" />
        </button>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          sideOffset={8}
          alignOffset={-50}
          collisionPadding={8}
          avoidCollisions
          className="max-h-[calc(100vh-16px)] overflow-y-auto min-w-[180px]
          max-w-[min(260px,var(--radix-popper-available-width))]
          bg-white rounded-lg p-2 border
          shadow-menu z-50
          text-brand-neutral-700 border-brand-neutral-200 scrollbar-thin"
        >
          {matters.map((matter) => (
            <DropdownMenu.Item
              key={matter.id}
              onClick={(e) => {
                e.stopPropagation()
                handleMatterSelect(matter.id, matter.name)
              }}
              className={clsx(
                'group flex items-center justify-between gap-2.5 p-2 rounded-md text-sm leading-6 w-full focus:outline-none cursor-pointer hover:bg-brand-neutral-100 hover:text-brand-500 focus:bg-brand-neutral-100 data-[highlighted]:bg-brand-neutral-100 data-[highlighted]:text-brand-500 mb-1 last:mb-0',
                fromMatterId === matter.id && 'bg-brand-neutral-100 text-brand-500 cursor-auto'
              )}
            >
              <span className="whitespace-nowrap group-hover:text-brand-500 group-focus:text-brand-500 truncate">{matter.name}</span>
              {fromMatterId === matter.id && <Check className="w-4 h-4 shrink-0 group-focus:text-brand-500 text-brand-500" />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  )
}
